/**
 * Story 052 — Account settings page.
 *
 * Single-page Settings surface with anchored sub-sections. The left
 * side renders a sub-nav list; clicking an item scrolls the right side
 * to the matching `<section id="...">`. Each section is its own
 * `role="region"` with an `aria-labelledby` heading so axe + screen
 * readers report them as distinct landmarks.
 *
 * IA per spec §"Page IA (single page, anchored sections)":
 *   Profile · Contact · Address · Security · Preferences · Privacy.
 *
 * The /account/profile placeholder redirects to /account/settings#profile
 * (wired in router.tsx). On mount, if location.hash names one of the
 * six section ids, scroll it into view.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import { useLocation } from "react-router-dom";
import { useExpandedMe } from "./queries/index.js";
import { ToastProvider } from "./ToastContext.js";
import { ProfileSection } from "./sections/ProfileSection.js";
import { ContactSection } from "./sections/ContactSection.js";
import { AddressSection } from "./sections/AddressSection.js";
import { SecuritySection } from "./sections/SecuritySection.js";
import { PreferencesSection } from "./sections/PreferencesSection.js";
import { PrivacySection } from "./sections/PrivacySection.js";

const SECTIONS = [
  { id: "profile", label: "Profile" },
  { id: "contact", label: "Contact" },
  { id: "address", label: "Address" },
  { id: "security", label: "Security" },
  { id: "preferences", label: "Preferences" },
  { id: "privacy", label: "Privacy" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

export function SettingsPage(): ReactElement {
  return (
    <ToastProvider>
      <SettingsPageInner />
    </ToastProvider>
  );
}

function SettingsPageInner(): ReactElement {
  const me = useExpandedMe();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState<SectionId>("profile");

  // On mount, if the URL has a hash matching a section, scroll there.
  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (SECTIONS.some((s) => s.id === hash)) {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ block: "start" });
        setActiveSection(hash as SectionId);
      }
    }
  }, [location.hash]);

  function jumpTo(id: SectionId) {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  if (me.isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (me.isError || !me.data) {
    return (
      <Box sx={{ py: 4 }}>
        <PageHeading sx={{ mb: 2 }}>Account settings</PageHeading>
        <Typography color="error">
          Couldn&apos;t load your account. Please try again.
        </Typography>
      </Box>
    );
  }

  const data = me.data;

  return (
    <Box>
      <PageHeading tabIndex={-1} sx={{ mb: 4, outline: "none" }}>
        Account settings
      </PageHeading>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={4}
        alignItems="flex-start"
      >
        <Box
          component="nav"
          aria-label="Settings sections"
          sx={{
            position: { md: "sticky" },
            top: { md: 16 },
            minWidth: { md: 200 },
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          {SECTIONS.map((s) => (
            <Button
              key={s.id}
              variant="text"
              onClick={() => {
                jumpTo(s.id);
              }}
              sx={(t) => ({
                justifyContent: "flex-start",
                color: "text.primary",
                textTransform: "none",
                px: 1.5,
                fontWeight: activeSection === s.id ? 600 : 400,
                bgcolor:
                  activeSection === s.id
                    ? t.palette.action.selected
                    : "transparent",
                "&:hover": {
                  bgcolor:
                    activeSection === s.id
                      ? t.palette.action.selected
                      : t.palette.action.hover,
                },
              })}
            >
              {s.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ProfileSection me={data} refetch={me.refetch} />
          <ContactSection me={data} />
          <AddressSection me={data} />
          <SecuritySection />
          <PreferencesSection />
          <PrivacySection />
        </Box>
      </Stack>
    </Box>
  );
}
